import {
  ColumnDef,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useMemo } from 'react';

import { CodeLanguageType } from '@/components/Code/types';
import { useDataGridSizingLocalStorage } from '@/components/DataGrid.utils';
import { GridPaginationModel } from '@/hooks/useDataGridState';
import { ExampleSchema } from '@/types/schema';

interface Headers {
  'x-pagination-total': number;
}

type RowData = ExampleSchema[] & {
  headers?: Headers | undefined;
};

type RowSelection = Record<string, boolean>;

interface UseExamplesTableProps {
  rows?: RowData;
  paginationModel: GridPaginationModel;
  onPaginationChange: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
  rowSelection?: RowSelection;
  onRowSelectionChange?: OnChangeFn<RowSelectionState> | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<ExampleSchema, any>[];
  setDeleteOpen?: React.Dispatch<React.SetStateAction<string | null>>;
  setEditOpen?: React.Dispatch<React.SetStateAction<ExampleSchema | null>>;
  sortingState?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  columnVisibilityModel?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  displayMode?: 'compact' | 'full';
  language?: CodeLanguageType;
}

export const useExamplesTable = ({
  rows,
  paginationModel,
  onPaginationChange,
  rowSelection,
  onRowSelectionChange,
  columns,
  setDeleteOpen,
  setEditOpen,
  sortingState,
  onSortingChange,
  columnVisibilityModel,
  onColumnVisibilityChange,
  displayMode = 'compact',
  language = 'json',
}: UseExamplesTableProps) => {
  'use no memo';
  const EMPTY_TABLE = [];

  const [columnSizing, setColumnSizing] =
    useDataGridSizingLocalStorage('examples');

  const hasAttachmentUrls = useMemo(() => {
    return (rows ?? []).some(
      (row) =>
        !!row.attachment_urls && Object.keys(row.attachment_urls).length > 0
    );
  }, [rows]);

  return useReactTable({
    columns: columns,
    data: rows ?? EMPTY_TABLE,
    state: {
      pagination: paginationModel,
      rowSelection,
      columnSizing,
      sorting: sortingState,
      columnVisibility: {
        attachment_urls: hasAttachmentUrls,
        ...columnVisibilityModel,
      },
    },
    meta: {
      actions: {
        handleDelete: setDeleteOpen,
        handleEdit: setEditOpen,
      },
      displayMode,
      language,
    },
    onPaginationChange,
    onRowSelectionChange,
    onColumnSizingChange: setColumnSizing,
    onSortingChange,
    onColumnVisibilityChange,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualExpanding: true,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    columnResizeMode: 'onChange',
    pageCount: Math.ceil(
      +(rows?.headers?.['x-pagination-total'] || 0) / paginationModel.pageSize
    ),
  });
};
